import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Button,
  ButtonGroup,
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  ModalFooter,
  SkeletonText,
  Spacer,
  Text,
  Textarea,
  useToast,
  useDisclosure
} from "@chakra-ui/react";

import client from "../../../../../helpers/Client";
import urlToId from "../../../../../helpers/UrlToId";

import { TaskState } from "../../../../../components/States";
import TaskActions from "../../../../../components/TaskActions";
import Notes from "../../../../../components/notes/Notes";
import formatDateTable from "../../../../../helpers/FormatDateTable";
import { ServiceListModalView } from "../../../Service/ServiceList/ServiceListModalView";
import InfoGrid from "../../../../../components/InfoGrid";
import CreateAppointmentModal from "./CreateAppointmentModal";
import CreateServiceModal from "./CreateServiceModal";
import { SubModalType } from "../Tasks";

function DescBox({ task, setTask }) {
  const [editing, setEditing] = useState(false);
  const [body, setBody] = useState(undefined);
  const [saving, setSaving] = useState(false);
  const handleSave = () => {
    setSaving(true);
    client.patch("/tasks/" + urlToId(task.url) + "/", { body }).then(() => {
      setTask({ ...task, ...{ body } });
      setSaving(false);
      setEditing(false);
    });
  };
  if (editing) {
    return (
      <Box borderWidth={1} p={2}>
        <Textarea
          mb={2}
          value={body || ""}
          onChange={(e) => setBody(e.target.value)}
        />
        <ButtonGroup variant="outline" size="sm" style={{ display: "flex" }}>
          <Spacer />
          <Button onClick={() => setEditing(false)}>Vazgeç</Button>
          <Button isLoading={saving} onClick={handleSave}>
            Kaydet
          </Button>
        </ButtonGroup>
      </Box>
    );
  } else {
    return (
      <Flex border="dashed 1px" flexDir="column" borderColor="gray.400" p={2} gap={2} fontWeight="400" fontSize="sm" onClick={() => { setBody(task.body); setEditing(true); }}>
        {task.body && task.body.split(/\r?\n/).map((line, i) => (<Text key={'task-desc-line-' + i}>{line}</Text>)) || "Bu alt görev için açıklama girilmemiş"}
      </Flex>
    );
  }
}

export default function TaskDetailModal({
  taskID,
  handleClose,
  handleAssign,
  handleSetType,
  handleSetAppointment,
  handleSetService,
  handleCreateSubtask,
  disclosure,
  ticketRendered,
  ticketRevalidator,
  activeSubModal,
  setActiveSubModal
}) {
  const toast = useToast();
  const [task, setTask] = useState({});
  const [services, setServices] = useState("");
  const createAppointmentModalState = useDisclosure();
  const createServiceModalState = useDisclosure();

  const subModalStateControllers = useMemo(() => ({
    [SubModalType.APPOINTMENT]: createAppointmentModalState,
    [SubModalType.SERVICE]: createServiceModalState,
    [SubModalType.NONE]: null
  }), [createAppointmentModalState, createServiceModalState])

  useEffect(() => {
    Object.entries(subModalStateControllers).forEach(([type, controller]) => {
      if (type === activeSubModal && controller) {
        controller.onOpen();
      } else if (controller) {
        controller.onClose(); 
      }
    });
  }, [activeSubModal, subModalStateControllers])

  const refresh = () => {
    client
      .get(`tasks/${taskID}/`)
      .then((response) => {
        setTask(response.data);
      })
      .catch((error) => {
        if (error.response.status === 404) {
          disclosure.onClose();
        }
      });
  };

  useEffect(() => {
    if (!task || !taskID) return;
    client.get(`service_records/?task=${taskID}`).then((response) => {
      setServices(response.data);
    });
  }, [taskID, task]);

  useEffect(() => {
    if (!taskID) return;
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskID, ticketRendered]);

  const handleAction = (apiPostfix, successMessage) => {
    client
      .post(`/tasks/${taskID}/${apiPostfix}/`, { confirm: true })
      .then((response) => {
        // POST 400 (Bad Request) is not an error, it is a not successful
        // response. catch fires when there is an error on request. So
        // We have to handle 400 responses here.
        if (response.status === 400) {
          Object.values(response.data).forEach((value) => {
            toast({
              title: "İşlem gerçekleştirilemiyor: " + value,
              status: "error",
            });
          });
          return;
        }
        toast({ title: successMessage, status: "success" });
        refresh();
        ticketRevalidator.revalidate();
      })
      .catch((error) => {
        Object.values(error.response.data).forEach((value) => {
          toast({
            title: "İşlem gerçekleştirilemiyor: " + value,
            status: "error",
          });
        });
      });
  };
  const isManager = localStorage.getItem("isManager") === "true";

  const handleDelete = () => handleAction("delete", "Görev silindi.");
  const handleStart = () => handleAction("start", "Görev başlatıldı");
  const handleComplete = () => handleAction("complete", "Görev tamamlandı");
  const handleApprove = () => handleAction("approve", "Görev onaylandı");
  const handleCancelAppointment = () => handleAction("cancel_appointment", "Randevu iptal edildi");

  const handleSubModalClose = () => {
    setActiveSubModal(SubModalType.NONE);
  };

  return (
    <Modal
      isOpen={disclosure.isOpen}
      placement="right"
      onClose={handleClose}
      size="2xl"
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <SkeletonText noOfLines={4} isLoaded={Object.keys(task).length > 0}>
            <Flex>
              <Text>Alt Görev #{task.number}</Text>
              <Spacer />
              <TaskState state={task.state} />
            </Flex>
          </SkeletonText>
        </ModalHeader>
        <ModalBody>
          <Flex flexDir={'column'} gap={4}>
            <Box>
              <Text as="h2" fontWeight="bold" mb={2}>
                {task.title}
              </Text>
              <DescBox {...{ task, setTask }} />
            </Box>
            {task.appointment_date && (
              <Text
                p={1}
                bg={'teal'}
                color={'white'}
                borderRadius={4}
                fontWeight={"bold"}
                textAlign={"center"}
              >
                Randevu: {formatDateTable(task.appointment_date)} saat{" "}
                {task.appointment_start_time} ile {task.appointment_end_time}{" "}
                arasında
              </Text>
            )}
            <TaskActions
              handleDelete={handleDelete}
              handleStart={handleStart}
              handleComplete={handleComplete}
              handleApprove={handleApprove}
              handleAssign={handleAssign}
              handleSetType={handleSetType}
              handleSetAppointment={handleSetAppointment}
              handleCancelAppointment={handleCancelAppointment}
              handleSetService={handleSetService}
              handleCreateSubtask={handleCreateSubtask}
              isManager={isManager}
              task={task}
            />
            <InfoGrid source={task} columns={3} keyMap={{
              'created': 'Oluşturulma',
              'assigned': 'Atanma',
              'started': 'Başlatılma',
              'completed': 'Tamamlanma',
              'approved': 'Onaylanma'
            }} />
            <ServiceListModalView services={services} />
            <Notes isNaive={true} object_model="ticket.task" object_id={taskID} header={"Alt Görev Notları"} />
          </Flex>
          <CreateAppointmentModal
            task={task}
            state={createAppointmentModalState}
            revalidator={ticketRevalidator}
            onClose={handleSubModalClose}
          />
          <CreateServiceModal
            task={task}
            state={createServiceModalState}
            revalidator={ticketRevalidator}
            onClose={handleSubModalClose}
          />
        </ModalBody>
        <ModalFooter>
          <Button variant="outline" onClick={ handleClose }>Kapat</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
